import React from "react";
import { Helmet } from "react-helmet-async";

const AdvancedSEOSchema = () => {
  // LocalBusiness Schema for Indian presence with GUSEC information
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://www.jyotixedge.com/#localbusiness",
    "name": "JyotixEdge Pvt. Ltd.",
    "alternateName": "JyotixEdge - GUSEC Incubated Startup",
    "description": "GUSEC incubated AI automation company specializing in restaurant intelligence platforms and operational efficiency solutions",
    "image": "https://www.jyotixedge.com/icons/apple-touch-icon.png",
    "url": "https://www.jyotixedge.com",
    "telephone": "+91-XXX-XXXXXXX",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "XXXX Business District",
      "addressLocality": "Ahmedabad",
      "addressRegion": "Gujarat",
      "postalCode": "XXXXXX",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 23.0225,
      "longitude": 72.5714
    },
    "foundingDate": "2024",
    "founder": {
      "@type": "Person",
      "name": "JyotixEdge Founding Team"
    },
    "memberOf": {
      "@type": "Organization",
      "name": "GUSEC - Gujarat University Startup & Entrepreneurship Council",
      "description": "Gujarat University's premier incubation center for startups"
    },
    "incubatedBy": {
      "@type": "Organization",
      "name": "GUSEC",
      "description": "Gujarat University Startup & Entrepreneurship Council",
      "url": "https://gusec.gujaratuniversity.ac.in"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday", 
        "Wednesday",
        "Thursday",
        "Friday"
      ],
      "opens": "09:00",
      "closes": "18:00"
    },
    "sameAs": [
      "https://linkedin.com/company/jyotixedge",
      "https://twitter.com/jyotixedge"
    ]
  };

  // Product Schema for R-Prismo
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": "https://www.jyotixedge.com/#rprismo-product",
    "name": "R-Prismo Restaurant Intelligence Platform",
    "description": "Complete restaurant intelligence platform with multilanguage support, maximum accuracy demand forecasting, and waste reduction analytics.",
    "brand": {
      "@type": "Brand",
      "name": "JyotixEdge"
    },
    "manufacturer": {
      "@type": "Organization",
      "name": "JyotixEdge Pvt. Ltd."
    },
    "category": "Restaurant Management Software",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "Contact for pricing",
      "priceCurrency": "INR",
      "seller": {
        "@type": "Organization",
        "name": "JyotixEdge Pvt. Ltd."
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "50"
    }
  };

  // Service Schema
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.jyotixedge.com/#ai-automation-service",
    "name": "AI Automation & Business Intelligence Services",
    "description": "Industry-specific AI automation platforms that reduce operational inefficiency by 25-30% and deliver ROI within 6 months.",
    "provider": {
      "@type": "Organization",
      "name": "JyotixEdge Pvt. Ltd."
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "India"
      },
      {
        "@type": "Country",
        "name": "United States"
      },
      {
        "@type": "Country",
        "name": "United Kingdom"
      }
    ],
    "serviceType": "AI Automation",
    "category": "Business Intelligence"
  };

  // HowTo Schema for Implementation
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Implement JyotixEdge AI Automation Platform",
    "description": "Step-by-step guide to implementing JyotixEdge AI automation solutions for maximum efficiency gains.",
    "totalTime": "PT12W",
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "INR",
      "value": "Contact for pricing"
    },
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Existing business systems (ERP, POS, CRM)"
      },
      {
        "@type": "HowToSupply", 
        "name": "Historical business data"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "JyotixEdge Platform"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Initial Consultation & Assessment",
        "text": "Contact JyotixEdge team for free consultation and comprehensive business needs assessment.",
        "position": 1
      },
      {
        "@type": "HowToStep",
        "name": "Custom Solution Design",
        "text": "Design industry-specific platform configuration based on your operational challenges and business goals.",
        "position": 2
      },
      {
        "@type": "HowToStep",
        "name": "Pilot Implementation",
        "text": "Deploy pilot program with API integrations, staff training, and initial AI model calibration over 8-12 weeks.",
        "position": 3
      },
      {
        "@type": "HowToStep",
        "name": "Full Deployment & Optimization",
        "text": "Scale to full implementation while continuously optimizing AI models to achieve maximum accuracy and measurable ROI.",
        "position": 4
      }
    ]
  };

  return (
    <Helmet>
      {/* Local Business Schema */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>
      
      {/* Product Schema */}
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>
      
      {/* Service Schema */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      
      {/* HowTo Schema */}
      <script type="application/ld+json">
        {JSON.stringify(howToSchema)}
      </script>
    </Helmet>
  );
};

export default AdvancedSEOSchema;